import React from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SubHeader from '../../components/Navbars/SubHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CARD_HEIGHT = '500px';

const getThemeTokens = (isLightTheme) => ({
  cardBg: isLightTheme ? '#ffffff' : '#27293d',
  cardBorder: isLightTheme ? '#eee' : '#3c3f58',
  headerBg: isLightTheme ? '#fff' : '#27293d',
  headerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  titleColor: isLightTheme ? '#333' : '#ffffff',
  rowColor: isLightTheme ? '#444' : 'rgba(255, 255, 255, 0.75)',
  valueColor: isLightTheme ? '#333' : '#ffffff',
  emphasisColor: isLightTheme ? '#000' : '#ffffff',
  dividerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  tableBorder: isLightTheme ? '#eee' : '#3c3f58',
  tableRowBorder: isLightTheme ? '#f9f9f9' : '#3c3f58',
  tableText: isLightTheme ? '#000' : 'rgba(255, 255, 255, 0.9)',
  tableHeader: isLightTheme ? '#333' : '#ffffff',
  scrollbarThumb: isLightTheme ? '#eee' : '#45486a',
  loadingOverlay: isLightTheme ? 'rgba(255,255,255,0.4)' : 'rgba(30, 30, 47, 0.55)',
  labelPluginFill: isLightTheme ? '#333' : '#cbd5e1',
  labelLineStroke: isLightTheme ? 'rgba(154, 154, 154, 0.6)' : 'rgba(148, 163, 184, 0.55)',
  emptyStateColor: isLightTheme ? '#9a9a9a' : '#94a3b8',
  legendColor: isLightTheme ? '#666' : '#ffffff',
  legendValueColor: isLightTheme ? '#333' : 'rgba(255, 255, 255, 0.85)',
  tickColor: isLightTheme ? '#333' : '#94a3b8',
  gridColor: isLightTheme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)',
  tooltipBg: isLightTheme ? 'rgba(255, 255, 255, 0.9)' : '#0f172a',
  tooltipTitle: isLightTheme ? '#333' : '#f1f5f9',
  tooltipBody: isLightTheme ? '#666' : '#cbd5e1',
  tooltipBorder: isLightTheme ? '#eee' : '#1e293b',
  shadow: isLightTheme ? '0 2px 4px rgba(0,0,0,0.05)' : '0 2px 8px rgba(0,0,0,0.25)',
});

const CHART_PALETTES = {
  sales: {
    light: ['#d1b975', '#6aa0c3', '#a8cbac', '#e7d9d0'],
    dark: ['#d1b975', '#6aa0c3', '#a8cbac', '#e7d9d0'],
  },
  loading: {
    light: ['#f0f0f0', '#f5f5f5', '#e9e9e9', '#efefef'],
    dark: ['#3c3f58', '#45486a', '#3a3d56', '#404362'],
  },
  location: {
    light: ['#e3f2fd', '#bbdefb', '#90caf9', '#0092e0'],
    dark: ['#1e3a5f', '#2563eb', '#3b82f6', '#60a5fa'],
  },
};

const IQDashboard = ({
  statistics,
  activeColor,
  isLoading,
  shellThemeVariant = '1',
  isLightTheme = false,
}) => {
  const theme = React.useMemo(() => getThemeTokens(isLightTheme), [isLightTheme]);
  const paletteKey = isLightTheme ? 'light' : 'dark';
  const chartColors = CHART_PALETTES.sales[paletteKey];
  const loadingChartColors = CHART_PALETTES.loading[paletteKey];
  const locationChartColors = CHART_PALETTES.location[paletteKey];

  const formatValue = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  };

  const normalizeData = (labels = [], values = [], isAvg = false, counts = []) => {
    const grouped = {};
    if (!labels || labels.length === 0) return { labels: ["-", "-", "-"], values: [0, 0, 0] };

    labels.forEach((label, i) => {
      if (!label) return;
      const canonical = label.trim().toLowerCase().split('-').map(part =>
        part.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      ).join('-');

      const val = values[i] || 0;
      const count = (counts && counts[i]) || 1;

      if (!grouped[canonical]) grouped[canonical] = { val: 0, count: 0 };

      if (isAvg) {
        grouped[canonical].val += val * count;
        grouped[canonical].count += count;
      } else {
        grouped[canonical].val += val;
      }
    });

    const resultLabels = Object.keys(grouped).sort((a, b) => grouped[b].val - grouped[a].val);
    const resultValues = resultLabels.map(l => {
      if (isAvg) return Math.round(grouped[l].val / (grouped[l].count || 1));
      return grouped[l].val;
    });
    return { labels: resultLabels, values: resultValues };
  };

  const chartAnimation = {
    duration: 1000,
    easing: 'easeOutQuart',
    delay: (context) => context.dataIndex * 100
  };

  const barDataLabelPlugin = React.useMemo(() => ({
    id: 'iq-datapoint-labels',
    afterDatasetsDraw: (chart) => {
      const { ctx, data } = chart;
      ctx.save();
      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = theme.labelPluginFill;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((bar, index) => {
        const val = data.datasets[0].data[index];
        if (val !== undefined) {
          ctx.fillText(`${val}`, bar.x + 5, bar.y);
        }
      });
      ctx.restore();
    }
  }), [theme.labelPluginFill]);

  const doughnutLabelsLine = React.useMemo(() => ({
    id: 'iqDoughnutLabelsLine',
    afterDatasetsDraw(chart) {
      const { ctx, data } = chart;
      ctx.save();
      const meta = chart.getDatasetMeta(0);

      meta.data.forEach((element, index) => {
        const value = data.datasets[0].data[index];
        if (!value || value === 0) return;

        const { x, y, startAngle, endAngle, outerRadius } = element;
        const middleAngle = startAngle + (endAngle - startAngle) / 2;

        const posX = x + Math.cos(middleAngle) * outerRadius;
        const posY = y + Math.sin(middleAngle) * outerRadius;

        const lineLen = 15;
        const endX = x + Math.cos(middleAngle) * (outerRadius + lineLen);
        const endY = y + Math.sin(middleAngle) * (outerRadius + lineLen);

        ctx.beginPath();
        ctx.moveTo(posX, posY);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = theme.labelLineStroke;
        ctx.lineWidth = 1;
        ctx.stroke();

        const horizontalLen = 10;
        const finalX = endX + (Math.cos(middleAngle) > 0 ? horizontalLen : -horizontalLen);
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(finalX, endY);
        ctx.stroke();

        const textValue = formatValue(value);
        ctx.font = "bold 11px Arial";
        ctx.fillStyle = theme.labelPluginFill;
        const isRightSide = Math.cos(middleAngle) > 0;
        ctx.textAlign = isRightSide ? "left" : "right";
        ctx.textBaseline = "middle";
        ctx.fillText(textValue, finalX + (isRightSide ? 8 : -8), endY);
      });
      ctx.restore();
    }
  }), [theme.labelLineStroke, theme.labelPluginFill]);

  const pastDueData = statistics?.accounts_past_due || {
    t0: 0, t1: 0, t2: 0, t3: 0, t4: 0, t5: 0, t6: 0, t7: 0, t8: 0
  };

  const pastDueLabels = [
    "Current",
    "T1 - Past Due 1 - 15",
    "T2 - Past Due 16 - 30",
    "T3 - Past Due 31 - 60",
    "T4 - Past Due 61 - 90",
    "T5 - Past Due 91 - 120",
    "T6 - Past Due > 120",
    "T7 - Repossession",
    "T8 - Bankruptcy"
  ];

  const pastDueValues = [
    pastDueData.t0 || 0,
    pastDueData.t1 || 0,
    pastDueData.t2 || 0,
    pastDueData.t3 || 0,
    pastDueData.t4 || 0,
    pastDueData.t5 || 0,
    pastDueData.t6 || 0,
    pastDueData.t7 || 0,
    pastDueData.t8 || 0,
  ];

  const pastDueColors = [
    "#38a336", "#4e9130", "#647f2a", "#796d24", "#8f5b1e", "#a54918", "#ba3712", "#d0250c", "#e61407"
  ];

  const totalPastDue = pastDueValues.reduce((a, b) => a + b, 0);

  const getSalesTodayData = () => {
    const period = statistics?.payments?.today || {
      data: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
      counts: { local_lender_finance: 0, in_house_finance: 0, cash: 0, wholesale: 0 },
      total_data: 0,
      total_counts: 0
    };

    const rowData = [
      { id: 'cash', label: 'Cash', rev: period.data?.cash || 0, count: period.counts?.cash || 0 },
      { id: 'in_house_finance', label: 'In-House', rev: period.data?.in_house_finance || 0, count: period.counts?.in_house_finance || 0 },
      { id: 'local_lender_finance', label: 'Outside', rev: period.data?.local_lender_finance || 0, count: period.counts?.local_lender_finance || 0 },
      { id: 'wholesale', label: 'Wholesale', rev: period.data?.wholesale || 0, count: period.counts?.wholesale || 0 },
    ];

    return {
      rows: rowData,
      labels: rowData.map(x => x.label),
      revs: rowData.map(x => x.rev),
      totalRev: period.total_data || 0,
      totalCount: period.total_counts || 0,
    };
  };

  const salesToday = getSalesTodayData();

  const getDailyRecapData = () => {
    const recap = statistics?.daily_recap || {
      counts: {},
      data: {},
      total_counts: 0,
      total_data: 0
    };

    const categories = [
      "Cash or Cash Equivalent",
      "Check or Check Equivalent",
      "EPS CC/ACH",
      "OTHER CC/ACH",
      "Other",
      "Unknown"
    ];

    const rows = categories.map(cat => ({
      label: cat,
      count: recap.counts?.[cat] || 0,
      amount: recap.data?.[cat] || 0
    }));

    return {
      rows,
      totalCount: recap.total_counts || 0,
      totalAmount: recap.total_data || 0
    };
  };

  const dailyRecap = getDailyRecapData();

  const getPortfolioStats = () => {
    const stats = statistics?.portfolio_stats || [];
    const totalActiveAccounts = stats.reduce((acc, curr) => acc + (curr.active_accounts || 0), 0);
    const totalPrincipalBalance = stats.reduce((acc, curr) => acc + (curr.principal_balance || 0), 0);
    const totalAccountBalance = stats.reduce((acc, curr) => acc + (curr.account_balance || 0), 0);

    return {
      stats,
      totalActiveAccounts,
      totalPrincipalBalance,
      totalAccountBalance
    };
  };

  const portfolioData = getPortfolioStats();

  const renderInventoryStats = (data) => (
    <div className="old-style-list">
      <div className="old-style-row"><span className="old-style-label"># Vehicles In Stock (Active)</span><span className="old-style-value">{data?.total_active_vehicles || 0}</span></div>
      <div className="old-style-row"><span className="old-style-label">Base Inventory Cost</span><span className="old-style-value">{formatValue(data?.base_inventory_cost)}</span></div>
      <div className="old-style-row"><span className="old-style-label">Expenses</span><span className="old-style-value">{formatValue(data?.total_expenses)}</span></div>
      <div className="old-style-row old-style-row--divider">
        <span className="old-style-label">Total Inventory Cost</span><span className="old-style-value old-style-value--emphasis">{formatValue((data?.base_inventory_cost || 0) + (data?.total_expenses || 0))}</span>
      </div>
      <div className="old-style-row"><span className="old-style-label">Vehicles Added Today</span><span className="old-style-value">{data?.added_today_count || 0}</span></div>
      <div className="old-style-row"><span className="old-style-label">Vehicle Value</span><span className="old-style-value">{formatValue(data?.added_today_value)}</span></div>
    </div>
  );

  const renderNoData = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.emptyStateColor, fontSize: '13px', fontWeight: '500' }}>
      No sales data available
    </div>
  );

  const renderIQCard = (title, content, hasStats = false, statsData = null, isNoData = false) => (
    <Col lg="3" md="6" className="mb-4">
      <Card className="iq-card">
        <div className="iq-header" style={{ position: 'relative' }}>
          <h4 className="iq-title">{title}</h4>
        </div>
        <CardBody className="iq-body">
          {isLoading && (
            <div className="loading-ghost-overlay">
              <div className="pulse-loading">Loading IQ...</div>
            </div>
          )}
          {hasStats && statsData && renderInventoryStats(statsData)}
          <div className="scrollable-chart-area">
            {(!isLoading && isNoData) ? (React.isValidElement(isNoData) ? isNoData : renderNoData()) : content}
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  return (
    <div className="iq-dashboard-container">
      <SubHeader
        activeColor={activeColor}
        back="/dashboard"
        title="IQ Dashboard"
        theme2ScrollGapFill={shellThemeVariant === '2'}
        isLightTheme={isLightTheme}
      />

      <style>{`
        .iq-card {
          background: ${theme.cardBg};
          border: 1px solid ${theme.cardBorder};
          height: ${CARD_HEIGHT};
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: ${theme.shadow};
        }
        .iq-header {
          padding: 15px 20px;
          border-bottom: 1px solid ${theme.headerBorder};
          background: ${theme.headerBg};
          text-align: center;
          flex-shrink: 0;
        }
        .iq-title { font-weight: 600; font-size: 13px; color: ${theme.titleColor}; margin: 0; }
        .iq-body { flex: 1; padding: 12px; overflow: hidden; position: relative; display: flex; flex-direction: column; color: ${theme.rowColor}; }
        
        .scrollable-chart-area { flex: 1; overflow-y: auto; overflow-x: hidden; padding-right: 5px; scrollbar-width: thin; min-height: 200px; }
        .scrollable-chart-area::-webkit-scrollbar { width: 5px; }
        .scrollable-chart-area::-webkit-scrollbar-thumb { background: ${theme.scrollbarThumb}; }

        .old-style-list { padding: 0px 5px 8px 5px; flex-shrink: 0; }
        .old-style-row { display: flex; justify-content: space-between; margin-bottom: 2px; font-size: 11px; color: ${theme.rowColor}; }
        .old-style-row--divider { border-bottom: 1px solid ${theme.dividerBorder}; padding-bottom: 3px; margin-bottom: 5px; }
        .old-style-label { font-weight: 400; }
        .old-style-value { font-weight: 600; color: ${theme.valueColor}; }
        .old-style-value--emphasis { color: ${theme.emphasisColor}; }

        .loading-ghost-overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: ${theme.loadingOverlay};
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          pointer-events: none;
        }
        
        .pulse-loading {
          font-weight: 700; font-size: 13px; color: #1d8cf8; text-transform: uppercase; letter-spacing: 2px;
          animation: iq-dashboard-pulse 1.5s infinite ease-in-out;
        }
        
        @keyframes iq-dashboard-pulse { 0% { opacity: 0.4; } 50% { opacity: 1; } 100% { opacity: 0.4; } }

        .past-due-legend-iq { 
          display: flex; 
          flex-direction: column; 
          gap: 6px; 
          max-height: 350px; 
          overflow-y: auto; 
          scrollbar-width: none; 
          padding-top: 10px;
        }
        .past-due-legend-iq::-webkit-scrollbar { display: none; }
        .legend-row-iq { display: flex; align-items: flex-start; gap: 8px; font-size: 10px; line-height: 1.2; }
        .legend-dot-iq { width: 10px; height: 10px; flex-shrink: 0; margin-top: 1px; border: 1px solid ${theme.cardBorder}; }
        .legend-label-iq { font-weight: 600; color: ${theme.legendColor}; }
        .legend-value-iq { color: ${theme.legendValueColor}; margin-left: 4px; }

        .sales-iq-list { margin-bottom: 8px; }
        .sales-iq-row { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 11px; }
        .sales-iq-row--totals { border-bottom: 1px solid ${theme.dividerBorder}; margin-bottom: 8px; padding-bottom: 4px; }
        .sales-iq-label { flex: 2; font-weight: 500; color: ${theme.rowColor}; }
        .sales-iq-count { flex: 1; text-align: center; font-weight: 600; color: ${theme.valueColor}; }
        .sales-iq-rev { flex: 2; text-align: right; font-weight: 600; color: ${theme.valueColor}; }

        .chart-container-border-iq { 
          height: 250px; 
          border: 1px solid ${theme.cardBorder}; 
          padding: 10px; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          margin-top: 10px;
        }

        .recap-table { width: 100%; border-collapse: collapse; margin-top: 5px; font-size: 11px; }
        .recap-table th { text-align: left; padding: 6px 2px; border-bottom: 1px solid ${theme.tableBorder}; color: ${theme.tableHeader}; font-weight: 700; white-space: nowrap; }
        .recap-table td { padding: 8px 2px; border-bottom: 1px solid ${theme.tableRowBorder}; color: ${theme.tableText}; font-weight: 500; }
        .recap-table .text-right { text-align: right; }
        .recap-table .text-center { text-align: center; }
        .recap-table .grand-total-row td { font-weight: 700; border-top: 1px solid ${theme.tableBorder}; border-bottom: none; padding-top: 12px; font-size: 12px; }

        .portfolio-summary { margin-bottom: 15px; padding: 0 5px; }
        .portfolio-summary-row { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 11px; color: ${theme.rowColor}; }
        .portfolio-summary-label { font-weight: 500; }
        .portfolio-summary-value { font-weight: 700; color: ${theme.valueColor}; }
      `}</style>

      <Row>
        {renderIQCard("Accounts Receivable By Portfolio", (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="portfolio-summary">
              <div className="portfolio-summary-row">
                <span className="portfolio-summary-label"># of Active Accounts</span>
                <span className="portfolio-summary-value">{portfolioData.totalActiveAccounts.toLocaleString()}</span>
              </div>
              <div className="portfolio-summary-row">
                <span className="portfolio-summary-label">Principal Balance</span>
                <span className="portfolio-summary-value">{formatValue(portfolioData.totalPrincipalBalance)}</span>
              </div>
              <div className="portfolio-summary-row">
                <span className="portfolio-summary-label ">Account Balance</span>
                <span className="portfolio-summary-value">{formatValue(portfolioData.totalAccountBalance)}</span>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: '300px' }}>
              <Bar
                data={{
                  labels: portfolioData.stats.map(s => s.name),
                  datasets: [
                    {
                      label: 'Total Principal',
                      data: portfolioData.stats.map(s => s.principal_balance),
                      backgroundColor: '#000080',
                      barThickness: 20,
                    },
                    {
                      label: 'Total Balance',
                      data: portfolioData.stats.map(s => s.account_balance),
                      backgroundColor: '#90EE90',
                      barThickness: 20,
                    }
                  ]
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 15,
                        font: { size: 11, weight: 'bold' },
                        color: theme.legendColor,
                        padding: 10,
                        generateLabels: (chart) => {
                          const labels = chart.data.datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.backgroundColor,
                            lineWidth: 0,
                            hidden: false,
                            index: i
                          }));
                          return labels;
                        }
                      }
                    },
                    tooltip: {
                      backgroundColor: theme.tooltipBg,
                      titleColor: theme.tooltipTitle,
                      bodyColor: theme.tooltipBody,
                      borderColor: theme.tooltipBorder,
                      borderWidth: 1,
                      padding: 10,
                      callbacks: {
                        label: (context) => `${context.dataset.label}: ${formatValue(context.raw)}`
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: { borderDash: [2, 2], drawBorder: false, color: theme.gridColor },
                      ticks: { font: { size: 10 }, color: theme.tickColor }
                    },
                    y: {
                      grid: { display: false },
                      ticks: { font: { size: 11, weight: 'bold' }, color: theme.tickColor }
                    }
                  }
                }}
              />
            </div>
          </div>
        ), false, null, portfolioData.stats.length === 0, true)}
        {renderIQCard("Sales Today", (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="sales-iq-list">
              <div className="sales-iq-row sales-iq-row--totals">
                <span className="sales-iq-label" style={{ fontWeight: 'bold' }}>Totals Sales</span>
                <span className="sales-iq-count">{salesToday.totalCount}</span>
                <span className="sales-iq-rev">{formatValue(salesToday.totalRev)}</span>
              </div>
              {salesToday.rows.map((row, index) => (
                <div key={index} className="sales-iq-row">
                  <span className="sales-iq-label">{row.label}</span>
                  <span className="sales-iq-count">{row.count}</span>
                  <span className="sales-iq-rev">{formatValue(row.rev)}</span>
                </div>
              ))}
            </div>
            <div className="chart-container-border-iq">
              <Doughnut
                key={isLoading ? 'loading' : 'ready'}
                data={{
                  labels: salesToday.labels,
                  datasets: [
                    {
                      data: salesToday.totalRev === 0 && isLoading ? [1, 1, 1, 1] : salesToday.revs,
                      backgroundColor: salesToday.totalRev === 0 && isLoading ? loadingChartColors : chartColors,
                      borderColor: theme.cardBg,
                      borderWidth: 2,
                    }
                  ]
                }}
                plugins={[doughnutLabelsLine]}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '70%',
                  layout: {
                    padding: { left: 65, right: 65, top: 5, bottom: 25 }
                  },
                  animation: {
                    duration: 1000,
                    easing: 'easeOutQuart',
                    delay: (context) => context.dataIndex * 150
                  },
                  plugins: {
                    legend: {
                      display: !isLoading,
                      position: 'top',
                      labels: {
                        padding: 5,
                        boxWidth: 10,
                        font: { size: 11 },
                        color: theme.legendColor,
                        usePointStyle: true,
                        pointStyle: 'rect',
                      }
                    },
                    tooltip: {
                      enabled: !isLoading,
                      callbacks: {
                        label: (context) => {
                          const label = context.label || '';
                          const val = context.raw || 0;
                          const total = salesToday.totalRev || 1;
                          const perc = ((val / total) * 100).toFixed(1);
                          return `${label}: ${formatValue(val)} (${perc}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        ), false, null, false, true)}

        {renderIQCard("Daily Recap", (
          <div style={{ height: '100%' }}>
            <table className="recap-table">
              <thead>
                <tr>
                  <th>Pymt Form Category</th>
                  <th className="text-center"># of Pymts</th>
                  <th className="text-right">Sum of Pymts</th>
                </tr>
              </thead>
              <tbody>
                {dailyRecap.rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.label}</td>
                    <td className="text-center">{row.count}</td>
                    <td className="text-right">{formatValue(row.amount)}</td>
                  </tr>
                ))}
                <tr className="grand-total-row">
                  <td>GRAND TOTALS</td>
                  <td className="text-center">{dailyRecap.totalCount}</td>
                  <td className="text-right">{formatValue(dailyRecap.totalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ), false, null, false, true)}

        {renderIQCard("Inventory By Location", (
          <div style={{ height: '100%' }}>
            {(() => {
              const raw = (statistics?.inventory_by_location || { labels: [], values: [] });
              const { labels: finalLabels, values: finalValues } = normalizeData(raw.labels, raw.values);
              const innerHeight = Math.max(220, finalLabels.length * 30);
              const maxVal = Math.max(...finalValues, 0);
              return (
                <div style={{ height: `${innerHeight}px`, position: 'relative' }}>
                  <Bar key={isLoading ? 'loading' : 'ready'}
                    data={{ labels: finalLabels, datasets: [{ data: finalValues, backgroundColor: locationChartColors, borderWidth: 0, barPercentage: 0.8, categoryPercentage: 0.9 }] }}
                    options={{
                      indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: chartAnimation,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: {
                          suggestedMax: (maxVal * 1.6) + 15,
                          grid: { borderDash: [2, 2], color: theme.gridColor },
                          ticks: { stepSize: 1, font: { size: 10 }, color: theme.tickColor, callback: (v) => Number.isInteger(v) ? v : null }
                        },
                        y: { grid: { display: false }, ticks: { font: { size: 10 }, color: theme.tickColor } }
                      }
                    }}
                    plugins={[barDataLabelPlugin]}
                  />
                </div>
              );
            })()}
          </div>
        ), true, statistics?.inventory_by_location, (statistics?.inventory_by_location?.values?.reduce((a, b) => a + b, 0) === 0))}

        {renderIQCard("Past Due Accounts", (
          <Row className="align-items-center mx-0" style={{ height: '100%' }}>
            <Col xs="7" className="px-0">
              <div style={{ height: '350px', position: 'relative' }}>
                <Doughnut
                  key={isLoading ? 'loading' : 'ready'}
                  data={{
                    labels: pastDueLabels,
                    datasets: [{
                      data: pastDueValues.every(v => v === 0) && isLoading ? [1, 1, 1] : pastDueValues,
                      backgroundColor: pastDueColors,
                      borderWidth: 0,
                    }]
                  }}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    cutout: '65%',
                    layout: { padding: 35 },
                    animation: { duration: 1000, easing: 'easeOutQuart', delay: (c) => c.dataIndex * 150 },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: "#1e1e2f",
                        titleColor: "#ffffff",
                        bodyColor: "#ffffff",
                        displayColors: true,
                        callbacks: {
                          label: function (context) {
                            const value = context.raw || 0;
                            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                            return `${context.label}: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                  plugins={[doughnutLabelsLine]}
                />
              </div>
            </Col>
            <Col xs="5" className="pl-2">
              <div className="past-due-legend-iq">
                {pastDueLabels.map((label, index) => {
                  const value = pastDueValues[index];
                  if (index === 0 && (!value || value === 0)) return null;
                  return (
                    <div key={index} className="legend-row-iq">
                      <div className="legend-dot-iq" style={{ backgroundColor: pastDueColors[index] }} />
                      <div className="legend-label-iq">
                        {label} {value > 0 && <span className="legend-value-iq">({value})</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        ), false, null, totalPastDue === 0)}
      </Row>
    </div>
  );
};
export default IQDashboard;
